import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import InternalBookingPage from "@components/InternalBookingPage";
import { ordresApi } from "@services/api/ordres";
import type { PendingAPrevoir } from "@services/api/ordres";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import PendingAPrevoirPanel from "@components/ui/PendingAPrevoirPanel";
import type { Ordre, OrdreIntervention } from "types/ordre";

interface SecondaryOrdreModalProps {
  ordre: Ordre;
  onClose: () => void;
}

/**
 * RDV secondaire = la prise de RDV interne, avec le client et le véhicule de l'ordre déjà
 * choisis : interventions, mobilité et confirmation au client comme un RDV normal.
 * Les travaux « à prévoir » du véhicule sont proposés avant, puis ajoutés au nouvel ordre.
 */
export default function SecondaryOrdreModal({ ordre, onClose }: SecondaryOrdreModalProps) {
  const [pending, setPending] = useState<PendingAPrevoir[] | null>(null);
  const [selectedPending, setSelectedPending] = useState<PendingAPrevoir[]>([]);
  const [booking, setBooking] = useState(false);
  const [newOrdreId, setNewOrdreId] = useState<string>("");

  const vin = ordre.vehicle?.vin;
  useEffect(() => {
    if (!ordre.dossier_id) { setPending([]); return; }
    ordresApi.getPendingAPrevoir({ dossier_id: ordre.dossier_id, vin, exclude_ordre_id: ordre.id })
      .then(setPending)
      .catch(() => setPending([]));
  }, [ordre.dossier_id, vin, ordre.id]);

  // Pas de travaux à prévoir : directement la prise de RDV.
  useEffect(() => {
    if (pending && pending.length === 0) setBooking(true);
  }, [pending]);

  const addAPrevoir = async (ordreId: string) => {
    if (selectedPending.length === 0) return;
    try {
      const created = await ordresApi.get(ordreId);
      const reproposed: OrdreIntervention[] = selectedPending.map((p, idx) => ({
        id: `i-${Date.now()}-${idx}`,
        label: p.intervention.label,
        estimated_hours: p.intervention.estimated_hours,
        ...(p.intervention.forfait_code ? { forfait_code: p.intervention.forfait_code } : {}),
        group: "initial",
        status: "inclus",
      }));
      await ordresApi.update(ordreId, { interventions: [...(created.interventions || []), ...reproposed] });
      await ordresApi.consumeAPrevoir(ordreId, selectedPending.map((p) => ({ ordre_id: p.ordre_id, intervention_id: p.intervention.id })));
    } catch (e) {
      console.error("Error adding à-prévoir lines:", e);
      showNotification({ type: "error", message: "RDV créé, mais les travaux à prévoir n'ont pas été ajoutés" });
    }
  };

  const close = () => {
    onClose();
    if (newOrdreId) navigateTo({ route: `/ordre/${newOrdreId}` });
  };

  if (booking) {
    // Ouverte depuis la fiche ordre (modale portée à 10000) : portée elle aussi sur le body,
    // après la fiche, sinon elle reste dessous à z-index égal.
    return createPortal(
      <div className="fixed inset-0 z-[10000] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
        <InternalBookingPage
          internalMode
          prefill={ordre.immatriculation ? {
            plate: ordre.immatriculation,
            marque: ordre.vehicle?.marque,
            modele: ordre.vehicle?.modele || ordre.modele,
            dossier_id: ordre.dossier_id,
            client_name: ordre.name,
          } : undefined}
          onClose={close}
          onSuccess={(id) => {
            if (!id) return;
            setNewOrdreId(id);
            addAPrevoir(id);
          }}
        />
      </div>,
      document.body,
    );
  }

  if (!pending) return null;

  return (
    <BaseModal
      title="Ajouter un RDV secondaire"
      startIcon={<Icon1 name="plus" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <PendingAPrevoirPanel
          dossierId={ordre.dossier_id}
          vin={vin}
          excludeOrdreId={ordre.id}
          onSelectionChange={setSelectedPending}
        />
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button variant="small" label="Continuer" onClick={() => setBooking(true)} />
      </div>
    </BaseModal>
  );
}
